import React, { useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import EqualizerIcon from '@mui/icons-material/InsertChartOutlined';

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import { estimateRiflePrecision, estimateRiflePrecisionSimple }  from '../lib/rifle-precision.js'
import toFixed from '../lib/toFixed'

function PrecisionEstimate() {
  const [ advanced, setAdvanced ] = useAutoSaveState('advanced', false)

  const [ rifleWeight, setRifleWeight ] = useAutoSaveState('rifleWeight', '')
  const [ bulletWeight, setBulletWeight ] = useAutoSaveState('bulletWeight', '')
  const [ powderWeight, setPowderWeight ] = useAutoSaveState('powderWeight', '')
  const [ velocity, setVelocity ] = useAutoSaveState('velocity', '')

  const [ bc, setBc ] = useAutoSaveState('bc', '')
  const [ twist, setTwist ] = useAutoSaveState('twist', '')
  const [ zoom, setZoom ] = useAutoSaveState('zoom', '')
  const [ imbalance, setImbalance ] = useAutoSaveState('imbalance', '.0001')
  const [ preview, setPreview ] = useAutoSaveState('preview', null)

  const [ result, setResult ] = useState('')

  const calculate = () => {
    let precisionResult
    if (advanced) {
      precisionResult = estimateRiflePrecision(
        parseFloat(velocity),
        parseFloat(bc),
        parseFloat(twist),
        parseFloat(imbalance),
        parseFloat(bulletWeight),
        parseFloat(powderWeight),
        parseFloat(rifleWeight),
        parseFloat(zoom)
      )
    } else {
       precisionResult = estimateRiflePrecisionSimple(
        parseFloat(velocity),
        parseFloat(bulletWeight),
        parseFloat(powderWeight),
        parseFloat(rifleWeight)
      )
    }
    setResult({ precision: precisionResult })
    setPreview(`Avg 5 Shot Group: ${toFixed(precisionResult, 3)} in`)
    window.gtag('event', 'calculate-precision-estimate')
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3} justifyContent="center" >
        <Grid container justifyContent="center" xs={12} style={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" gutterBottom>
            Rifle Precision Estimation
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />
        <Grid item xs={8}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Bullet Weight" autoFocus value={bulletWeight} onChange={e => setBulletWeight(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">grains</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Bullet Velocity" value={velocity} onChange={e => setVelocity(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">fps</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Powder Weight" value={powderWeight} onChange={e => setPowderWeight(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">grains</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Rifle Weight" value={rifleWeight} onChange={e => setRifleWeight(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">lbs</InputAdornment> }} />
              </Grid>
              {advanced && <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="G7 BC" value={bc} onChange={e => setBc(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Bullet CG Offset" value={imbalance} onChange={e => setImbalance(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">in</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Twist Rate" value={twist} onChange={e => setTwist(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">in/rev</InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Zoom" value={zoom} onChange={e => setZoom(e.target.value)} />
                </Grid>
              </>}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={advanced}
                      onChange={e => setAdvanced(e.target.checked)}
                    />}
                  label="Advanced Mode"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={8}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Button variant="contained" onClick={calculate}>Calculate</Button>
            </Grid>
          </Paper>
        </Grid>
        {result && <Grid item xs={8}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Typography variant="h5" gutterBottom>
                <EqualizerIcon /> Results
              </Typography>
            </Grid>
            <Divider style={{ marginBottom: '20px', marginTop: '20px' }} />
            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="h5"><b>Average 5 Shot Group:</b> {toFixed(result.precision, 3)} in</Typography>
            </Box>
          </Paper>
        </Grid>}
      </Grid>
    </Container>
  );
}

export default function WrappedPrecisionEstimation() {
  return (
    <AutoSaveProvider>
      <PrecisionEstimate />
    </AutoSaveProvider>
  )
}
